import type {
  ManifestVideoWall,
  VideoWallSlicedAsset,
  VideoWallActiveLayout,
  VideoWallLayout,
  VideoWallScheduleSlot,
  ManifestAsset,
  DeviceManifest,
} from '../types';
import { syncManager } from './sync-manager';

/**
 * VideoWallManager handles video wall mode for devices that are part of a multi-screen video wall.
 *
 * Key responsibilities:
 * - Determine if the device is part of a video wall
 * - Get the correct sliced asset for this device's position (or group position)
 * - Apply bezel compensation offsets
 * - Coordinate synchronized playback start times
 * - Support dynamic layouts with groups and schedule-based switching
 */
class VideoWallManager {
  private config: ManifestVideoWall | null = null;
  private slicedAssetMap: Map<string, VideoWallSlicedAsset> = new Map();
  private syncStartTime: number | null = null;
  private activeLayout: VideoWallActiveLayout | null = null;
  private layouts: VideoWallLayout[] = [];
  private layoutSchedule: VideoWallScheduleSlot[] = [];
  private scheduleDelayTimer: ReturnType<typeof setTimeout> | null = null;
  private scheduleIntervalTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Initialize the video wall manager with manifest configuration
   */
  initialize(manifest: DeviceManifest): boolean {
    if (!manifest.videoWall) {
      this.config = null;
      this.slicedAssetMap.clear();
      this.activeLayout = null;
      this.layouts = [];
      this.layoutSchedule = [];
      console.log('[VideoWall] Device is not part of a video wall');
      return false;
    }

    this.config = manifest.videoWall;
    this.activeLayout = manifest.videoWall.activeLayout || null;
    this.layouts = manifest.videoWall.layouts || [];
    this.layoutSchedule = manifest.videoWall.layoutSchedule || [];

    console.log('[VideoWall] Initialized for video wall:', this.config.name);
    console.log('[VideoWall] Position:', `Row ${this.config.row}, Column ${this.config.column}`);
    console.log('[VideoWall] Grid:', `${this.config.totalColumns}x${this.config.totalRows}`);
    console.log('[VideoWall] Sync method:', this.config.syncMethod);

    if (this.activeLayout) {
      console.log('[VideoWall] Active layout:', this.activeLayout.layoutName);
      console.log('[VideoWall] Group:', this.activeLayout.group.name,
        `(local: ${this.activeLayout.group.localRow},${this.activeLayout.group.localColumn})`);
    }

    // Build sliced asset map (sourceAssetId -> sliced asset for this position)
    this.buildSlicedAssetMap();

    // Set up schedule evaluation timer
    if (this.layoutSchedule.length > 0) {
      this.startScheduleEvaluation();
    }

    return true;
  }

  /**
   * Build a map of source asset IDs to sliced assets for this device's position.
   * If a layout group is active, use group-level slices; otherwise use wall-level slices.
   */
  private buildSlicedAssetMap(): void {
    this.slicedAssetMap.clear();

    if (!this.config) return;

    // First, try group-level slices from active layout
    if (this.activeLayout?.group?.slicedAssets) {
      for (const slicedAsset of this.activeLayout.group.slicedAssets) {
        this.slicedAssetMap.set(slicedAsset.sourceAssetId, slicedAsset);
        console.log('[VideoWall] Mapped group slice for asset:', slicedAsset.sourceAssetId);
      }
    }

    // Then add wall-level slices (as fallback for assets without group slices)
    for (const slicedAsset of this.config.slicedAssets) {
      if (slicedAsset.row === this.config.row && slicedAsset.column === this.config.column) {
        if (!this.slicedAssetMap.has(slicedAsset.sourceAssetId)) {
          this.slicedAssetMap.set(slicedAsset.sourceAssetId, slicedAsset);
          console.log('[VideoWall] Mapped wall slice for asset:', slicedAsset.sourceAssetId);
        }
      }
    }

    console.log('[VideoWall] Total sliced assets mapped:', this.slicedAssetMap.size);
  }

  /**
   * Check if the device is part of a video wall
   */
  isVideoWallMode(): boolean {
    return this.config !== null;
  }

  /**
   * Get the video wall configuration
   */
  getConfig(): ManifestVideoWall | null {
    return this.config;
  }

  /**
   * Get the device's position in the video wall grid
   */
  getPosition(): { row: number; column: number } | null {
    if (!this.config) return null;
    return { row: this.config.row, column: this.config.column };
  }

  /**
   * Check if this device is the master (position 0,0)
   */
  isMaster(): boolean {
    if (!this.config) return false;
    return this.config.row === 0 && this.config.column === 0;
  }

  /**
   * Get the sliced asset URL for a given source asset ID
   * Returns the original asset URL if no slice is available
   */
  getSlicedAssetUrl(sourceAssetId: string, originalUrl: string): string {
    const slicedAsset = this.slicedAssetMap.get(sourceAssetId);
    if (slicedAsset) {
      console.log('[VideoWall] Using sliced asset for:', sourceAssetId);
      return slicedAsset.url;
    }
    console.log('[VideoWall] No slice found, using original asset:', sourceAssetId);
    return originalUrl;
  }

  /**
   * Get the sliced asset for a given source asset ID
   */
  getSlicedAsset(sourceAssetId: string): VideoWallSlicedAsset | undefined {
    return this.slicedAssetMap.get(sourceAssetId);
  }

  /**
   * Transform an asset for video wall display
   * Returns a modified asset with the sliced URL and dimensions
   */
  transformAsset(asset: ManifestAsset): ManifestAsset {
    const slicedAsset = this.slicedAssetMap.get(asset.id);

    if (slicedAsset) {
      return {
        ...asset,
        url: slicedAsset.url,
        sha256: slicedAsset.sha256,
        size: slicedAsset.fileSize,
        width: slicedAsset.width,
        height: slicedAsset.height,
      };
    }

    return asset;
  }

  /**
   * Get bezel compensation offsets for CSS positioning
   * Returns CSS transform values to apply bezel compensation
   */
  getBezelCompensation(): { offsetX: number; offsetY: number } {
    if (!this.config) {
      return { offsetX: 0, offsetY: 0 };
    }

    const { bezelCompensation, calibration, row, column } = this.config;

    // Calculate total offset including bezel compensation and calibration
    const bezelOffsetX = column * bezelCompensation.horizontal;
    const bezelOffsetY = row * bezelCompensation.vertical;

    return {
      offsetX: bezelOffsetX + calibration.offsetX,
      offsetY: bezelOffsetY + calibration.offsetY,
    };
  }

  /**
   * Get CSS styles to apply for video wall display region
   */
  getDisplayStyles(): Record<string, string> {
    if (!this.config) {
      return {};
    }

    const offsets = this.getBezelCompensation();

    return {
      transform: `translate(${offsets.offsetX}px, ${offsets.offsetY}px)`,
      transformOrigin: 'top left',
    };
  }

  /**
   * Calculate the synchronized start time for playback
   * Uses NTP sync or software sync based on configuration
   */
  async calculateSyncStartTime(): Promise<number> {
    if (!this.config) {
      return Date.now();
    }

    switch (this.config.syncMethod) {
      case 'ntp':
        // Use NTP-synchronized time
        return this.getNtpSyncedTime();

      case 'software':
        // Software sync - coordinate via WebSocket
        return this.getSoftwareSyncTime();

      case 'hardware_genlock':
      case 'hardware_sender':
        // Hardware sync - rely on external hardware for sync
        // Player just starts immediately, hardware handles synchronization
        return Date.now();

      default:
        return Date.now();
    }
  }

  /**
   * Get NTP-synchronized time for precise sync
   * In practice, this would use an NTP client or server-provided time offset
   */
  private getNtpSyncedTime(): number {
    // Use clock offset from SyncManager (calibrated via heartbeat server time)
    const correctedNow = syncManager.getCorrectedTime();
    const nextSecond = Math.ceil(correctedNow / 1000) * 1000;
    return nextSecond + 1000; // Start at next whole second + 1 second buffer
  }

  /**
   * Get software-synchronized time
   * Coordinates with master device via WebSocket
   */
  private getSoftwareSyncTime(): number {
    // If we have a pending sync time from the master, use it
    if (this.syncStartTime && this.syncStartTime > Date.now()) {
      return this.syncStartTime;
    }

    // Otherwise, calculate a sync time
    // Round to 5-second boundary for basic sync
    const now = Date.now();
    const nextBoundary = Math.ceil(now / 5000) * 5000;
    return nextBoundary + 5000; // Start at next 5-second boundary + buffer
  }

  /**
   * Set the synchronized start time (received from master device)
   */
  setSyncStartTime(timestamp: number): void {
    this.syncStartTime = timestamp;
    console.log('[VideoWall] Sync start time set:', new Date(timestamp).toISOString());
  }

  /**
   * Wait until the synchronized start time
   */
  async waitForSyncStart(): Promise<void> {
    const targetTime = await this.calculateSyncStartTime();
    const waitTime = targetTime - Date.now();

    if (waitTime > 0) {
      console.log('[VideoWall] Waiting', waitTime, 'ms for sync start');
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }

  /**
   * Get the active layout information
   */
  getActiveLayout(): VideoWallActiveLayout | null {
    return this.activeLayout;
  }

  /**
   * Get the group configuration for this device (if a layout is active)
   */
  getGroupConfig(): {
    localRow: number;
    localColumn: number;
    spanRows: number;
    spanColumns: number;
    playlistId: string | null;
  } | null {
    if (!this.activeLayout) return null;
    const g = this.activeLayout.group;
    return {
      localRow: g.localRow ?? 0,
      localColumn: g.localColumn ?? 0,
      spanRows: g.spanRows,
      spanColumns: g.spanColumns,
      playlistId: g.playlistId ?? null,
    };
  }

  /**
   * Get the display region for this device within the full video wall content.
   * If a layout group is active, returns the region within the group; otherwise
   * returns the region within the full wall.
   */
  getDisplayRegion(): { x: number; y: number; width: number; height: number } | null {
    if (!this.config) return null;

    const { totalRows, totalColumns, totalWidth, totalHeight, bezelCompensation } = this.config;

    // Calculate per-screen dimensions
    const screenWidth = totalWidth / totalColumns;
    const screenHeight = totalHeight / totalRows;

    if (this.activeLayout) {
      // Group-aware: compute viewport within the group
      const g = this.activeLayout.group;
      const localRow = g.localRow ?? 0;
      const localCol = g.localColumn ?? 0;

      const x = localCol * screenWidth - (localCol * bezelCompensation.horizontal);
      const y = localRow * screenHeight - (localRow * bezelCompensation.vertical);

      return {
        x,
        y,
        width: screenWidth,
        height: screenHeight,
      };
    }

    // No layout: full wall mode
    const { row, column } = this.config;
    const x = column * screenWidth - (column * bezelCompensation.horizontal);
    const y = row * screenHeight - (row * bezelCompensation.vertical);

    return {
      x,
      y,
      width: screenWidth,
      height: screenHeight,
    };
  }

  /**
   * Start periodic schedule evaluation to detect layout transitions
   */
  private startScheduleEvaluation(): void {
    // Check every 30 seconds for schedule changes
    const checkSchedule = () => {
      const newLayoutId = this.evaluateSchedule();
      if (newLayoutId && newLayoutId !== this.activeLayout?.layoutId) {
        console.log('[VideoWall] Schedule transition: switching to layout', newLayoutId);
        this.switchToLayout(newLayoutId);
      }
    };

    // Calculate time until next minute boundary for more precise scheduling
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    // First check at next minute boundary, then every 30 seconds
    this.scheduleDelayTimer = setTimeout(() => {
      checkSchedule();
      this.scheduleDelayTimer = null;
      this.scheduleIntervalTimer = setInterval(checkSchedule, 30_000);
    }, msUntilNextMinute);
  }

  /**
   * Parse RRULE string to extract BYDAY (days of week).
   * Returns array of JS day numbers: 0=Sunday, 1=Monday, ..., 6=Saturday
   */
  private getDaysFromRRule(rrule: string): number[] {
    const bydayMatch = rrule.match(/BYDAY=([^;]+)/i);
    if (!bydayMatch) {
      return [0, 1, 2, 3, 4, 5, 6]; // All days if no BYDAY
    }

    const dayMap: Record<string, number> = {
      SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6,
    };

    const dayStrs = bydayMatch[1].split(',');
    const days = dayStrs
      .map((d) => dayMap[d.trim().toUpperCase()])
      .filter((d) => d !== undefined);

    return days.length > 0 ? days : [0, 1, 2, 3, 4, 5, 6];
  }

  /**
   * Evaluate the layout schedule and return the active layout ID
   */
  private evaluateSchedule(): string | null {
    if (this.layoutSchedule.length === 0) return null;

    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    // Sort by priority (highest first)
    const sorted = [...this.layoutSchedule].sort((a, b) => b.priority - a.priority);

    for (const slot of sorted) {
      // Use explicit days if provided, otherwise parse from RRULE
      const days = slot.days.length > 0
        ? slot.days
        : slot.rrule
          ? this.getDaysFromRRule(slot.rrule)
          : [0, 1, 2, 3, 4, 5, 6];

      if (!days.includes(currentDay)) continue;

      // Time range check (handles overnight ranges)
      const [startH, startM] = slot.startTime.split(':').map(Number);
      const [endH, endM] = slot.endTime.split(':').map(Number);
      const [curH, curM] = currentTime.split(':').map(Number);

      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      const curMinutes = curH * 60 + curM;

      let inRange: boolean;
      if (endMinutes > startMinutes) {
        inRange = curMinutes >= startMinutes && curMinutes <= endMinutes;
      } else {
        // Overnight range (e.g., 22:00 - 06:00)
        inRange = curMinutes >= startMinutes || curMinutes <= endMinutes;
      }

      if (inRange) {
        return slot.layoutId;
      }
    }

    // No schedule match — return default layout
    const defaultLayout = this.layouts.find((l) => l.isDefault);
    return defaultLayout?.id || null;
  }

  /**
   * Switch to a different layout by ID
   */
  private switchToLayout(layoutId: string): void {
    if (!this.config) return;

    const layout = this.layouts.find((l) => l.id === layoutId);
    if (!layout) return;

    // Find which group this device belongs to in the new layout
    const group = layout.groups.find(
      (g) =>
        this.config!.row >= g.startRow &&
        this.config!.row < g.startRow + g.spanRows &&
        this.config!.column >= g.startColumn &&
        this.config!.column < g.startColumn + g.spanColumns
    );

    if (!group) {
      console.warn('[VideoWall] Device not covered by any group in layout:', layout.name);
      return;
    }

    this.activeLayout = {
      layoutId: layout.id,
      layoutName: layout.name,
      group: {
        ...group,
        localRow: this.config.row - group.startRow,
        localColumn: this.config.column - group.startColumn,
      },
    };

    // Rebuild sliced asset map for new group
    this.buildSlicedAssetMap();

    console.log('[VideoWall] Switched to layout:', layout.name, 'group:', group.name);
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.scheduleDelayTimer) {
      clearTimeout(this.scheduleDelayTimer);
      this.scheduleDelayTimer = null;
    }
    if (this.scheduleIntervalTimer) {
      clearInterval(this.scheduleIntervalTimer);
      this.scheduleIntervalTimer = null;
    }
    this.config = null;
    this.slicedAssetMap.clear();
    this.syncStartTime = null;
    this.activeLayout = null;
    this.layouts = [];
    this.layoutSchedule = [];
  }
}

export const videoWallManager = new VideoWallManager();
